export const DASHBOARD_BRIEFING_PLAN_VERSION = 2;

const MAX_DASHBOARD_BRIEFING_SECTIONS = 24;
const DASHBOARD_BRIEFING_SECTION_ID_RE = /^[a-z0-9][a-z0-9_-]{0,79}$/;
const DASHBOARD_BRIEFING_NARRATION_SPORT_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const ALLOWED_DASHBOARD_BRIEFING_SECTION_FIELDS = new Set([
  "articles",
  "body",
  "id",
  "narrationMatches",
  "narrationSport",
  "quoteAuthor",
  "quoteText",
  "tasks",
  "title",
]);

function record(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalizedTargetMinutes(value) {
  return Number(value) === 5 ? 5 : 2;
}

function isNewsSectionId(id) {
  return (
    id === "news" ||
    id === "news_highlights" ||
    id.startsWith("news_") ||
    id.startsWith("category-") ||
    id === "local-news-gemini"
  );
}

function isSportsSectionId(id) {
  return (
    id === "cricket" ||
    id === "sports" ||
    id === "sports_country_primary" ||
    id.startsWith("sport_")
  );
}

function expectedNarrationSport(id) {
  if (id === "cricket" || id === "sport_cricket") return "cricket";
  if (!id.startsWith("sport_")) return "";
  const sport = id.slice("sport_".length);
  return sport === "soccer" ? "football" : sport;
}

function hasUsableArticle(section) {
  return (
    Array.isArray(section.articles) &&
    section.articles.some(
      (article) =>
        article &&
        typeof article === "object" &&
        !Array.isArray(article) &&
        typeof article.title === "string" &&
        article.title.trim(),
    )
  );
}

function isStrictV2ReadySection(section) {
  const fields = Object.keys(section);
  if (fields.some((field) => !ALLOWED_DASHBOARD_BRIEFING_SECTION_FIELDS.has(field))) {
    return false;
  }
  const id = section.id;
  if (isNewsSectionId(id) && !hasUsableArticle(section)) return false;
  if (
    (id === "quote" || id === "quote_of_the_day") &&
    (typeof section.quoteText !== "string" || !section.quoteText.trim())
  ) {
    return false;
  }
  if (!isSportsSectionId(id)) return true;
  const narrationSport = section.narrationSport;
  const expectedSport = expectedNarrationSport(id);
  return Boolean(
    typeof narrationSport === "string" &&
      DASHBOARD_BRIEFING_NARRATION_SPORT_RE.test(narrationSport) &&
      (!expectedSport || narrationSport === expectedSport) &&
      Array.isArray(section.narrationMatches) &&
      section.narrationMatches.length > 0,
  );
}

/**
 * Build the strict Dashboard request shared by the Home Dashboard and Brief
 * entry surfaces. Structured sections are the sole content authority; legacy
 * summaries, top-level section ids, and articles must never enter a v2 body.
 */
export function buildDashboardBriefingV2Payload(input = {}) {
  const facts = record(input.facts);
  const briefSections = Array.isArray(facts.briefSections)
    ? facts.briefSections.slice(0, MAX_DASHBOARD_BRIEFING_SECTIONS)
    : [];
  return {
    dashboardBriefingPlanVersion: DASHBOARD_BRIEFING_PLAN_VERSION,
    country: String(input.country || "")
      .trim()
      .toUpperCase(),
    targetMinutes: normalizedTargetMinutes(input.targetMinutes),
    lang: String(input.lang || "en")
      .trim()
      .toLowerCase(),
    sourceId: String(input.sourceId || "").trim(),
    categoryLabel: String(input.categoryLabel || "").trim(),
    facts: { briefSections },
    voiceId:
      String(input.voiceId || "auto")
        .trim()
        .toLowerCase() || "auto",
  };
}

function exactRequestedSectionIds(request) {
  if (
    !request ||
    typeof request !== "object" ||
    request.dashboardBriefingPlanVersion !== DASHBOARD_BRIEFING_PLAN_VERSION
  ) {
    return null;
  }
  const sections = record(request.facts).briefSections;
  if (
    !Array.isArray(sections) ||
    sections.length === 0 ||
    sections.length > MAX_DASHBOARD_BRIEFING_SECTIONS
  ) {
    return null;
  }
  const ids = [];
  const seen = new Set();
  for (const section of sections) {
    const sectionRecord = record(section);
    const rawId = sectionRecord.id;
    if (
      typeof rawId !== "string" ||
      !DASHBOARD_BRIEFING_SECTION_ID_RE.test(rawId) ||
      seen.has(rawId) ||
      !isStrictV2ReadySection(sectionRecord)
    ) {
      return null;
    }
    seen.add(rawId);
    ids.push(rawId);
  }
  return ids;
}

export function hasExactDashboardBriefingV2Plan(request) {
  return exactRequestedSectionIds(request) !== null;
}

export function normalizeDashboardBriefingV2Payload(input) {
  const payload = buildDashboardBriefingV2Payload(input);
  return hasExactDashboardBriefingV2Plan(payload) ? payload : null;
}

/**
 * Accept a Dashboard response only when it attests the exact immutable v2
 * section plan and supplies a coherent timing interval for every section.
 */
export function attestsExactDashboardBriefingV2Plan(response, request) {
  if (
    !response ||
    typeof response !== "object" ||
    response.dashboardBriefingPlanVersion !== DASHBOARD_BRIEFING_PLAN_VERSION
  ) {
    return false;
  }
  const expectedIds = exactRequestedSectionIds(request);
  const audioSections = response.audioSections;
  if (
    !expectedIds ||
    !Array.isArray(audioSections) ||
    audioSections.length !== expectedIds.length
  ) {
    return false;
  }
  let priorEndMs = 0;
  for (let index = 0; index < audioSections.length; index += 1) {
    const section = record(audioSections[index]);
    const startMs = section.startMs;
    const endMs = section.endMs;
    if (
      section.id !== expectedIds[index] ||
      !Number.isInteger(startMs) ||
      !Number.isInteger(endMs) ||
      startMs < priorEndMs ||
      endMs <= startMs
    ) {
      return false;
    }
    priorEndMs = endMs;
  }
  return true;
}

export function assertDashboardBriefingV2Attested(response, request) {
  if (attestsExactDashboardBriefingV2Plan(response, request)) return;
  const error = new Error("dashboard_audio_attestation_unavailable");
  error.status = 0;
  error.code = "dashboard_audio_attestation_unavailable";
  throw error;
}
